import { useSupabaseClientStore } from '../../stores/supaBaseClientStore'
import { useAuthStore } from '../../stores/authStore'
import { useLogger } from '../../composables/useLogger'
import CryptoJS from 'crypto-js'
import QRCode from 'qrcode'

const logger = useLogger()

export class QrCode {
    id: string;
    created_at: string | null;
    used_at: string | null;
    user_id: string;

    constructor(id: string, userId: string) {
        this.id = id;
        this.user_id = userId;
        this.created_at = null;
        this.used_at = null;
    }

    // Getter methods to convert string timestamps to Date objects
    get createdAt(): Date | null {
        return this.created_at ? new Date(this.created_at) : null;
    }

    get usedAt(): Date | null {
        return this.used_at ? new Date(this.used_at) : null;
    }

    // Setter methods to convert Date objects to ISO strings
    set createdAt(date: Date | null) {
        this.created_at = date ? date.toISOString() : null;
    }

    set usedAt(date: Date | null) {
        this.used_at = date ? date.toISOString() : null;
    }

    // Static method to get the supabase client
    static getSupabase() {
        const supabaseClientStore = useSupabaseClientStore();
        return supabaseClientStore.supabase;
    }

    // Method to check if QR code is valid (not used and not older than 24 hours)
    isValid(): boolean {
        if (this.used_at) {
            return false; // Already used
        }
        
        if (!this.created_at) {
            return false; // No creation date
        }
        
        const creationTime = new Date(this.created_at).getTime();
        const currentTime = new Date().getTime();
        const hoursDiff = (currentTime - creationTime) / (1000 * 60 * 60);
        
        return hoursDiff < 24; // Valid if less than 24 hours old
    }

    // Method to mark QR code as used
    async markAsUsed(): Promise<boolean> {
        logger.debug('QrCode: Marking QR code as used', { qrCodeId: this.id });
        
        const { data, error } = await QrCode.getSupabase()
            .from('qr_codes_senior')
            .update({ used_at: new Date().toISOString() })
            .eq('id', this.id)
            .select()
            .maybeSingle();

        if (error) {
            logger.error('QrCode: Error marking QR code as used', { error, qrCodeId: this.id });
            logger.throwSupabaseError(error, `Failed to mark QR code ${this.id} as used`);
        }

        if (!data) {
            logger.error('QrCode: QR code not found when marking as used', { qrCodeId: this.id });
            return false;
        }

        this.used_at = data.used_at;
        logger.debug('QrCode: Marked as used', { qrCodeId: this.id });
        return true;
    }

    // Method to get encrypted QR string
    getEncryptedQrString(): string {
        const authStore = useAuthStore();
        const encryptedId = authStore.encryptQrCodeId(this.id);
        return `nestor:${encryptedId}`;
    }

    // Method to generate QR code data URL
    async generateQrCodeDataUrl(): Promise<string> {
        try {
            const qrValue = this.getEncryptedQrString();
            
            // Generate QR code as data URL
            const dataUrl = await QRCode.toDataURL(qrValue, {
                width: 400,
                margin: 1,
                color: {
                    dark: '#1a202c',
                    light: '#ffffff'
                }
            });
            
            return dataUrl;
        } catch (err) {
            logger.logErrorCaught('Failed to generate QR code', 'QrCode.ts', err);
            throw err;
        }
    }

    // Static method to get QR code by ID
    static async fromId(id: string): Promise<QrCode | null> {
        logger.debug('QrCode: Getting QR code by ID', { id });
        
        const { data, error } = await QrCode.getSupabase()
            .from('qr_codes_senior')
            .select('*')
            .eq('id', id)
            .maybeSingle();

        if (error) {
            logger.throwSupabaseError(error, `Failed to get QR code with ID ${id}`);
        }

        if (!data) {
            logger.error('QrCode: No QR code found for ID', { id });
            return null;
        }

        return await QrCode.fromDatabaseRow(data);
    }

    // Static method to create QR code from database row
    static async fromDatabaseRow(row: any): Promise<QrCode> {
        const qrCode = new QrCode(row.id, row.user_id);
        qrCode.created_at = row.created_at;
        qrCode.used_at = row.used_at;
        return qrCode;
    }

    // Static method to create a new QR code
    static async createNew(userId: string): Promise<QrCode> {
        logger.debug('QrCode: Creating new QR code', { userId });
        
        const { data, error } = await QrCode.getSupabase()
            .from('qr_codes_senior')
            .insert({
                user_id: userId,
                used_at: null
            })
            .select()
            .single();

        if (error) {
            logger.error('QrCode: Error creating QR code', { error, userId });
            logger.throwSupabaseError(error, `Failed to create QR code for user ${userId}`);
        }

        if (!data) {
            logger.error('QrCode: No data returned after creating QR code', { userId });
            throw new Error('No data returned after creating QR code');
        }

        logger.debug('QrCode: Created new QR code', { qrCodeId: data.id });
        return await QrCode.fromDatabaseRow(data);
    }

    // Static method to find a valid QR code for a user
    static async findValidForUser(userId: string): Promise<QrCode | null> {
        logger.debug('QrCode: Finding valid QR code for user', { userId });
        
        // Calculate the timestamp for 24 hours ago
        const twentyFourHoursAgo = new Date();
        twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);
        
        const { data, error } = await QrCode.getSupabase()
            .from('qr_codes_senior')
            .select('*')
            .eq('user_id', userId)
            .is('used_at', null)
            .gte('created_at', twentyFourHoursAgo.toISOString())
            .order('created_at', { ascending: false })
            .maybeSingle();

        if (error) {
            logger.throwSupabaseError(error, `Failed to find valid QR code for user ${userId}`);
        }

        if (!data) {
            logger.debug('QrCode: No valid QR code found for user', { userId });
            return null;
        }

        logger.debug('QrCode: Found valid QR code', { qrCodeId: data.id });
        return await QrCode.fromDatabaseRow(data);
    }

    // Static method to get or create a valid QR code
    static async getOrCreateValidQrCode(userId: string): Promise<QrCode> {
        // First try to find an existing valid QR code
        const existingQrCode = await QrCode.findValidForUser(userId);
        
        if (existingQrCode) {
            logger.debug('QrCode: Using existing valid QR code', { qrCodeId: existingQrCode.id });
            return existingQrCode;
        }
        
        // No valid QR code found, create a new one
        logger.debug('QrCode: No valid QR code found, creating new one', { userId });
        return await QrCode.createNew(userId);
    }

    // Static method to get QR code from encrypted string
    static async getQrCodeFromEncryptedString(encryptedString: string): Promise<QrCode | null> {
        try {
            const authStore = useAuthStore();
            const qrCodeId = authStore.decryptQrCodeId(encryptedString);
            
            if (!qrCodeId) {
                logger.error('QrCode: Failed to decrypt QR code ID', { encryptedString });
                return null;
            }
            
            return await QrCode.fromId(qrCodeId);
        } catch (error) {
            logger.logErrorCaught('QrCode: Error decrypting QR code string', 'QrCode.ts', error);
            return null;
        }
    }
} 